import { StrictMode, Suspense, lazy, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom'
import { StoreProvider } from './lib/store'
import './styles.css'

import Home from './pages/Home'
import Shop from './pages/Shop'
import ProductPage from './pages/Product'
import CartPage from './pages/Cart'
import Checkout from './pages/Checkout'
import Quiz from './pages/Quiz'
import About from './pages/About'
import { Journal, JournalPost } from './pages/Journal'
import { Account, ForgotPassword, Login, NotFound, OrderPage, ResetPassword, TrackOrder } from './pages/Account'
import { Privacy, Terms } from './pages/Legal'

import { Loading } from './components/Layout'
import { CartToast } from './components/CartToast'

// The admin panel loads as its own chunk, so shoppers never download it.
const AdminShell = lazy(() => import('./admin/AdminShell'))
const Dashboard = lazy(() => import('./admin/Dashboard'))
const AdminProducts = lazy(() => import('./admin/Products'))
const AdminOrders = lazy(() => import('./admin/Orders'))
const Ambassadors = lazy(() => import('./admin/Ambassadors'))
const Customers = lazy(() => import('./admin/Content').then((m) => ({ default: m.Customers })))
const Faqs = lazy(() => import('./admin/Content').then((m) => ({ default: m.Faqs })))
const AdminJournal = lazy(() => import('./admin/Content').then((m) => ({ default: m.Journal })))
const Marketing = lazy(() => import('./admin/Content').then((m) => ({ default: m.Marketing })))
const Reviews = lazy(() => import('./admin/Content').then((m) => ({ default: m.Reviews })))
const Settings = lazy(() => import('./admin/Content').then((m) => ({ default: m.Settings })))

/** Restores the top of the page on navigation, and honours in-page anchors. */
function ScrollManager() {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
        return
      }
    }
    window.scrollTo(0, 0)
  }, [pathname, hash])

  return null
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <StoreProvider>
        <ScrollManager />
        <CartToast />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:slug" element={<ProductPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/quiz" element={<Quiz />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/journal/:slug" element={<JournalPost />} />
          <Route path="/about" element={<About />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/account" element={<Account />} />
          <Route path="/order/:reference" element={<OrderPage />} />
          <Route path="/track" element={<TrackOrder />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />

          <Route
            path="/admin"
            element={
              <Suspense fallback={<Loading label="Loading admin" />}>
                <AdminShell />
              </Suspense>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="ambassadors" element={<Ambassadors />} />
            <Route path="customers" element={<Customers />} />
            <Route path="reviews" element={<Reviews />} />
            <Route path="journal" element={<AdminJournal />} />
            <Route path="faqs" element={<Faqs />} />
            <Route path="marketing" element={<Marketing />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </StoreProvider>
    </BrowserRouter>
  </StrictMode>
)
